/**
 * Validates if an image meets Instagram's aspect ratio requirements
 * Instagram requires aspect ratios between 0.8:1 and 1.91:1
 * (approximately 4:5 portrait to 1.91:1 landscape)
 * 
 * @param {File|string} image - Image file or image URL
 * @returns {Promise<{isValid: boolean, aspectRatio?: number, error?: string}>}
 */
export const validateInstagramImageAspectRatio = async (image) => {
  return new Promise((resolve) => {
    const img = new Image();
    let timeoutId;
    
    // Set a timeout for image loading (15 seconds)
    timeoutId = setTimeout(() => {
      console.warn('⚠️ Image validation timeout - image took too long to load');
      resolve({
        isValid: true, // Don't block if timeout - let backend handle it
        warning: 'Image validation timed out. The image will be validated by the server when posting.'
      });
    }, 15000);
    
    // Set up error handler
    img.onerror = (error) => {
      clearTimeout(timeoutId);
      console.warn('⚠️ Image validation error:', error);
      console.warn('⚠️ Image source:', typeof image === 'string' ? image : 'File object');
      
      // If it's a CORS error or network error, don't block - let backend handle it
      if (error && (error.type === 'error' || error.message?.includes('CORS'))) {
        console.log('ℹ️ CORS or network error detected - allowing validation to proceed');
        resolve({
          isValid: true, // Don't block on CORS errors - backend will validate
          warning: 'Could not validate image due to CORS restrictions. The image will be validated by the server when posting.'
        });
        return;
      }
      
      resolve({
        isValid: true, // Don't block on frontend validation errors - backend will handle it
        warning: 'Unable to load image for validation in browser. The image will be validated by the server when posting.'
      });
    };
    
    // Set up success handler
    img.onload = () => {
      clearTimeout(timeoutId);
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      
      console.log(`📐 Image dimensions: ${width}x${height}`);
      
      if (!width || !height || width === 0 || height === 0) {
        console.warn('⚠️ Invalid image dimensions:', { width, height });
        resolve({
          isValid: true, // Don't block - let backend handle it
          warning: 'Unable to determine image dimensions in browser. The image will be validated by the server when posting.'
        });
        return;
      }
      
      // Calculate aspect ratio (width/height)
      const aspectRatio = width / height;
      console.log(`📐 Aspect ratio: ${aspectRatio.toFixed(2)}:1`);
      
      // Instagram requires aspect ratios between 0.8:1 and 1.91:1
      const minAspectRatio = 0.8;
      const maxAspectRatio = 1.91;
      
      const isValid = aspectRatio >= minAspectRatio && aspectRatio <= maxAspectRatio;
      
      if (!isValid) {
        console.warn(`❌ Invalid aspect ratio: ${aspectRatio.toFixed(2)}:1 (required: ${minAspectRatio}:1 to ${maxAspectRatio}:1)`);
        resolve({
          isValid: false,
          aspectRatio: aspectRatio,
          error: `Image aspect ratio (${aspectRatio.toFixed(2)}:1) is not supported by Instagram. Instagram requires images with aspect ratios between ${minAspectRatio}:1 and ${maxAspectRatio}:1 (approximately 4:5 portrait to 1.91:1 landscape). Please use a different image with a valid aspect ratio.`
        });
      } else {
        console.log('✅ Image aspect ratio is valid for Instagram');
        resolve({
          isValid: true,
          aspectRatio: aspectRatio
        });
      }
    };
    
    // Set image source
    if (typeof image === 'string') {
      // It's a URL
      // Try with CORS first, but don't fail if it doesn't work
      img.crossOrigin = 'anonymous'; // Handle CORS if needed
      img.src = image;
    } else if (image instanceof File) {
      // It's a File object
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => {
        clearTimeout(timeoutId);
        resolve({
          isValid: true, // Don't block - let backend handle it
          warning: 'Unable to read image file in browser. The image will be validated by the server when posting.'
        });
      };
      reader.readAsDataURL(image);
    } else {
      clearTimeout(timeoutId);
      resolve({
        isValid: false,
        error: 'Invalid image format. Please provide a File object or image URL.'
      });
    }
  });
};

/**
 * Validates if an image URL meets Instagram's aspect ratio requirements
 * This is a convenience function for URL-based validation
 * 
 * @param {string} imageUrl - Image URL to validate
 * @returns {Promise<{isValid: boolean, aspectRatio?: number, error?: string}>}
 */
export const validateInstagramImageUrl = async (imageUrl) => {
  if (!imageUrl) {
    return {
      isValid: false,
      error: 'Image URL is required.'
    };
  }
  return validateInstagramImageAspectRatio(imageUrl);
};

/**
 * Validates if an image file meets Instagram's aspect ratio requirements
 * This is a convenience function for File-based validation
 * 
 * @param {File} imageFile - Image file to validate
 * @returns {Promise<{isValid: boolean, aspectRatio?: number, error?: string}>}
 */
export const validateInstagramImageFile = async (imageFile) => {
  if (!imageFile) {
    return {
      isValid: false,
      error: 'Image file is required.'
    };
  }
  return validateInstagramImageAspectRatio(imageFile);
};

/**
 * Resizes an image to Instagram's recommended 1:1 format (1080x1080) and converts to JPEG
 * This auto-fixes AI images to avoid aspect ratio errors
 * 
 * @param {File|string} image - Image file or image URL
 * @returns {Promise<Blob>} - Resized image as JPEG Blob (1080x1080)
 */
export const resizeForInstagram = (image) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    let timeoutId;
    
    // Set timeout for image loading (30 seconds)
    timeoutId = setTimeout(() => {
      reject(new Error('Image loading timeout. Please try again.'));
    }, 30000);
    
    img.onerror = (error) => {
      clearTimeout(timeoutId);
      console.error('Error loading image for resize:', error);
      reject(new Error('Failed to load image for resizing. Please check the image URL or file.'));
    };
    
    img.onload = () => {
      clearTimeout(timeoutId);
      
      try {
        // Get image dimensions - use naturalWidth/Height for accurate dimensions
        const imgWidth = img.naturalWidth || img.width || 1080;
        const imgHeight = img.naturalHeight || img.height || 1080;
        
        console.log(`📐 Original image dimensions: ${imgWidth}x${imgHeight}`);
        
        // Validate that we have some dimensions
        if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0) {
          console.warn('⚠️ Invalid dimensions detected, using default 1080x1080');
          // Use default dimensions and create a solid color image
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const SIZE = 1080;
          canvas.width = SIZE;
          canvas.height = SIZE;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, SIZE, SIZE);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                console.log('✅ Created default 1080x1080 image');
                resolve(blob);
              } else {
                reject(new Error('Failed to create default image.'));
              }
            },
            "image/jpeg",
            0.9
          );
          return;
        }
        
        // Check for extreme aspect ratios - still try to resize, but log warning
        const aspectRatio = imgWidth / imgHeight;
        if (aspectRatio > 100 || aspectRatio < 0.01) {
          console.warn('⚠️ Extreme aspect ratio detected:', aspectRatio.toFixed(2), 'Image dimensions:', imgWidth, 'x', imgHeight);
          console.warn('⚠️ Attempting to fix by resizing to 1080x1080...');
        }
        
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        
        // Instagram recommended size: 1080x1080 (1:1)
        const SIZE = 1080;
        canvas.width = SIZE;
        canvas.height = SIZE;
        
        // Calculate center crop (crop to square from center)
        // Use the smaller dimension to ensure we get a square
        const minSide = Math.min(imgWidth, imgHeight);
        const sx = Math.max(0, (imgWidth - minSide) / 2);
        const sy = Math.max(0, (imgHeight - minSide) / 2);
        
        // Ensure we don't exceed image bounds
        const sourceWidth = Math.min(minSide, imgWidth - sx);
        const sourceHeight = Math.min(minSide, imgHeight - sy);
        
        // Draw image centered and cropped to square
        // If dimensions are extreme, this will still create a valid square image
        ctx.drawImage(
          img,
          sx,           // Source X (crop start)
          sy,           // Source Y (crop start)
          sourceWidth,  // Source width (square crop)
          sourceHeight, // Source height (square crop)
          0,            // Destination X
          0,            // Destination Y
          SIZE,         // Destination width (1080)
          SIZE          // Destination height (1080)
        );
        
        // Convert to JPEG with 0.9 quality (good balance between quality and size)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log('✅ Image resized to 1080x1080 (1:1) for Instagram');
              resolve(blob);
            } else {
              reject(new Error('Failed to convert image to JPEG.'));
            }
          },
          "image/jpeg",
          0.9
        );
      } catch (error) {
        console.error('Error resizing image:', error);
        reject(new Error('Failed to resize image. Please try again.'));
      }
    };
    
    // Set image source
    if (typeof image === 'string') {
      // It's a URL
      img.src = image;
    } else if (image instanceof File) {
      // It's a File object
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error('Failed to read image file.'));
      };
      reader.readAsDataURL(image);
    } else {
      clearTimeout(timeoutId);
      reject(new Error('Invalid image format. Please provide a File object or image URL.'));
    }
  });
};

/**
 * Converts a Blob to a File object
 * 
 * @param {Blob} blob - Blob to convert
 * @param {string} filename - Filename for the File object
 * @returns {File} - File object
 */
export const blobToFile = (blob, filename = 'instagram-image.jpg') => {
  return new File([blob], filename, { type: 'image/jpeg' });
};

